/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import React, { memo } from 'react';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { MenuSection, MenuSectionHeading } from '@atlaskit/side-nav-items/menu-section';
import { MenuList } from '@atlaskit/side-nav-items/menu-list';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import LinkMenuItem from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/ui/link-menu-item-with-analytics/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import type { Features } from '../../common/types.tsx';
import { COMMON_ANALYTICS_ATTRIBUTES } from './constants.tsx';

const heading = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.operations-section.heading',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Operations',
	description:
		'Section heading for the Operations group in the global products settings sidebar navigation. Displayed above links to operations-related configuration pages.',
});

const shiftFromOpsgenieToJiraItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.operations-section.shift-from-opsgenie-to-jira-item',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Move from Opsgenie to Jira',
	description: 'Sidebar option to shift from opsgenie to jira page.',
});

const newLozenge = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.operations-section.new-lozenge',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'New',
	description:
		'Lozenge label displayed next to a navigation item in the operations settings sidebar to indicate that the feature is newly available.',
});

const chatopsToolsItem = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-settings-global-products.operations-section.chatops-tools-item',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Chat and video tools',
	description: 'Sidebar option to open chatops tools page',
});

type Props = {
	features: Features;
};

const OperationsSection = ({ features }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const currentRoute = useCurrentRoute();

	return (
		<MenuSection isMenuListItem>
			<MenuSectionHeading>{formatMessage(heading)}</MenuSectionHeading>
			<MenuList>
				{features?.hasShiftFromOpsgenieToJira && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'moveFromOpsgenieToJira' }}
						isSelected={currentRoute.name === 'eoc-unlock-operations'}
						href="/jira/settings/products/ops/unlock"
						elemAfter={<Lozenge appearance="new">{formatMessage(newLozenge)}</Lozenge>}
					>
						{formatMessage(shiftFromOpsgenieToJiraItem)}
					</LinkMenuItem>
				)}
				{features?.hasChatOpsToolAccess && (
					<LinkMenuItem
						analytics={{ ...COMMON_ANALYTICS_ATTRIBUTES, itemId: 'jsm-chatops-tools' }}
						href="/jira/settings/products/ops/jira-service-management-chatops-and-video-tools"
						isSelected={currentRoute.name === 'products-jsm-chatops-and-videos-tools'}
					>
						{formatMessage(chatopsToolsItem)}
					</LinkMenuItem>
				)}
			</MenuList>
		</MenuSection>
	);
};

const ExportedComponent: React.MemoExoticComponent<({ features }: Props) => React.JSX.Element> =
	memo(OperationsSection);
export default ExportedComponent;
